import mermaid from 'mermaid';
import { fixMermaidSyntax } from './mermaidSyntaxFixer';

// Track render attempts for unique IDs — prevents Mermaid v11 ID collisions
let renderCounter = 0;

// Initialize mermaid config
mermaid.initialize({
  startOnLoad: false, // Prevent auto-run — we control rendering manually
  theme: 'default',
  securityLevel: 'loose',
  fontSize: 16,
  flowchart: {
    useMaxWidth: false,
    htmlLabels: true,
    curve: 'basis',
    diagramPadding: 20,
    nodeSpacing: 50,
    rankSpacing: 50,
    padding: 20,
    wrappingWidth: 200
  }
});

/**
 * Setup global resize function that diagrams can call
 * @param {number} zoomLevel - Current zoom level
 */
export const setupMermaidResize = () => {
  window.mermaidResize = (svg) => {
    // No-op: CSS zoom on the inner container handles layout sizing correctly.
    // No manual inline style adjustments needed.
  };
};

/**
 * Render a single Mermaid diagram element
 * @param {HTMLElement} element - The diagram element to render
 * @param {number} zoomLevel - Current zoom level
 * @returns {Promise<void>}
 */
export const renderSingleDiagram = async (element, zoomLevel) => {
  const elementId = element.id;
  const isProcessed = element.getAttribute('data-processed') === 'true';

  if (isProcessed) return;

  try {
    const code = element.getAttribute('data-code');
    if (!code) {
      element.innerHTML = `<div class="error-message">Error: No Mermaid code found</div>`;
      return;
    }

    if (typeof mermaid === 'undefined' || !mermaid.render) {
      element.innerHTML = `<div class="error-message">Error: Mermaid library not properly loaded</div>`;
      return;
    }

    // Use unique render ID per attempt — Mermaid v11 creates temp DOM elements
    // that can persist and conflict if the same ID is reused
    renderCounter++;
    const uniqueRenderId = `mermaid-svg-${elementId}-${renderCounter}`;

    // Clean up any leftover Mermaid temp containers from previous render attempts
    document.querySelectorAll(`[id^="dmermaid-svg-${elementId}"]`).forEach(el => el.remove());

    let svgCode;
    try {
      svgCode = await mermaid.render(uniqueRenderId, fixMermaidSyntax(code));
      if (!svgCode || !svgCode.svg) {
        element.innerHTML = `<div class="error-message" style="padding: 20px; border: 1px solid #ffcccc; background-color: #fff8f8; border-radius: 4px; color: #d32f2f; font-family: monospace; white-space: pre-wrap; word-break: break-word; margin-bottom: ${zoomLevel > 1.5 ? '100px' : '40px'};">Error: Invalid SVG generated</div>`;
        return;
      }
    } catch (renderErr) {
      // Mermaid v11 throws transient DOM errors (e.g. "Cannot read properties of null
      // (reading 'firstChild')") when the container isn't fully laid out yet (accordion
      // still animating). Don't show these to the user — leave element unprocessed so
      // the next scheduled render attempt can retry successfully.
      const isTransientDOMError = renderErr.message && (
        renderErr.message.includes('firstChild') ||
        renderErr.message.includes('Cannot read properties of null') ||
        renderErr.message.includes('is not a function')
      );

      if (isTransientDOMError) {
        console.warn(`Transient render error for ${elementId}, will retry:`, renderErr.message);
        return; // Leave data-processed="false" for retry
      }

      // Only show persistent errors (syntax errors, etc.) to the user
      console.error(`Error rendering diagram ${elementId}:`, renderErr);
      element.innerHTML = `<div class="error-message" style="padding: 20px; border: 1px solid #ffcccc; background-color: #fff8f8; border-radius: 4px; color: #d32f2f; font-family: monospace; white-space: pre-wrap; word-break: break-word; margin-bottom: ${zoomLevel > 1.5 ? '100px' : '40px'};">Error rendering diagram: ${renderErr.message}</div>`;
      return;
    }

    const svgWithOnload = svgCode.svg.replace('<svg ', '<svg onload="(function(){if(window.mermaidResize)window.mermaidResize(this);})();" ');
    element.innerHTML = svgWithOnload;
    element.setAttribute('data-processed', 'true');

    // Let the container auto-size based on content and CSS zoom
    // No need to set inline minHeight as zoom property handles layout correctly
  } catch (err) {
    // Same transient error check for the outer catch
    const isTransientDOMError = err.message && (
      err.message.includes('firstChild') ||
      err.message.includes('Cannot read properties of null') ||
      err.message.includes('is not a function')
    );

    if (isTransientDOMError) {
      console.warn(`Transient render error for ${elementId}, will retry:`, err.message);
      return; // Leave data-processed="false" for retry
    }

    console.error(`Error rendering diagram ${elementId}:`, err);
    const errorMargin = zoomLevel > 1.5 ? '120px' : zoomLevel > 1.2 ? '80px' : '40px';
    let errorMessage = err.message;

    if (err.message && err.message.includes('parse')) {
      errorMessage = err.message
        .replace(/^.*?Parse error on line/, 'Parse error on line')
        .replace(/\n/g, '<br/>');
    }

    element.innerHTML = `
      <div class="error-message" style="
        padding: 20px;
        border: 1px solid #ffcccc;
        background-color: #fff8f8;
        border-radius: 4px;
        color: #d32f2f;
        font-family: monospace;
        white-space: pre-wrap;
        word-break: break-word;
        margin-bottom: ${errorMargin};
        line-height: 1.5;
        overflow-wrap: break-word;
      ">
        <strong>Error rendering diagram:</strong><br/><br/>
        ${errorMessage}
      </div>
    `;
  }
};

/**
 * Render all Mermaid diagrams on the page
 * @param {number} zoomLevel - Current zoom level
 * @returns {Promise<void>}
 */
export const renderMermaidDiagrams = async (zoomLevel = 1) => {
  try {
    const diagramElements = document.querySelectorAll('.mermaid-diagram');

    if (diagramElements.length === 0) {
      return;
    }

    // Setup resize function
    setupMermaidResize(zoomLevel);

    // Render each diagram
    for (const element of diagramElements) {
      await renderSingleDiagram(element, zoomLevel);
    }
  } catch (error) {
    console.error('Error in renderMermaidDiagrams function:', error);
  }
};

/**
 * Reset all diagrams to unprocessed state (for re-rendering)
 */
export const resetDiagramProcessing = () => {
  const diagramElements = document.querySelectorAll('.mermaid-diagram');
  diagramElements.forEach(element => {
    element.setAttribute('data-processed', 'false');
  });
};
